/**
 * What the practice console may show, read from the tenant's capability map.
 *
 * The map is `GET /api/tenant/self/capabilities` (docs/architecture/plan-entitlements-
 * contract.md, "Resolution"):
 *
 *   { plan_id: "flow" | null,
 *     capabilities: {
 *       "crm.contacts_360":        { granted: true,  status: "live",        eta: null, source: "plan", core: false, kind: "boolean", ... },
 *       "ai_agent.assistant":      { granted: true,  status: "coming_soon", eta: "Q1 2027", ... },
 *       "practice.products_limit": { granted: true,  status: "live", kind: "limit", value: 25, used: 3, ... },
 *     } }
 *
 * `granted` is snapshot MEMBERSHIP (the tenant bought it), and `status` is whether the
 * feature exists at all. The server refuses a coming-soon capability even for a tenant
 * that bought it (status before grant), so "usable" is the conjunction of the two — a
 * page that read `granted` alone would open onto a surface that 503s.
 *
 * Pure functions, no React and no transport: the context (EntitlementsContext.js), the
 * provider, the route guard and the tests all read the map through these, so there is
 * one definition of "usable", "coming soon" and "unlimited".
 */

/** The capability keys the console reads. Values are the backend registry keys. */
export const CAP = Object.freeze({
  crm: "crm.contacts_360",
  aiAssistant: "ai_agent.assistant",
  automation: "automation.rules",
  social: "social.scheduling",
  socialPublishing: "social.publishing",
  videoRooms: "care.video_rooms",
  courses: "learn.courses",
  customDomain: "site.custom_domain",
  bookingsLimit: "practice.bookings_limit",
  productsLimit: "practice.products_limit",
  // crm-module BEGIN C3.02/C3.04 — Promote (lane F4). Booster-granted, gates no route:
  // `granted(CAP.promotion)` only decides whether PromoteModal's step 2 shows "owned"
  // or the "Buy booster" call to action (crm-frontend-design.md §B.2).
  promotion: "marketplace.promotion",
  // crm-module END
});

export const CAPABILITIES_PATH = "/tenant/self/capabilities";

/** The wire's "no ceiling" value for a limit. */
export const UNLIMITED = -1;

const STATUS_LIVE = "live";

/**
 * Where a capability stands for this tenant:
 *   "granted"     — bought and built: the page opens;
 *   "coming_soon" — not built yet (or unknown to the catalogue, which the server also
 *                   answers as CAPABILITY_NOT_BUILT): nothing to buy yet;
 *   "not_in_plan" — built, but this tenant's plan does not include it.
 */
export function capabilityState(capabilities, key) {
  const entry = capabilities?.[key];
  if (!entry || entry.status !== STATUS_LIVE) return "coming_soon";
  return entry.granted === true ? "granted" : "not_in_plan";
}

/** The published ETA of a coming-soon capability, `null` when none is stated. */
export function capabilityEta(capabilities, key) {
  const eta = capabilities?.[key]?.eta;
  return typeof eta === "string" && eta.trim() ? eta : null;
}

/**
 * A limit capability's numbers: `{ value, used }` where `value === UNLIMITED` means no
 * ceiling. `null` when the map carries no usable number for the key — the caller then
 * states nothing and leaves the refusal to the server, which owns the count.
 */
export function capabilityLimit(capabilities, key) {
  const entry = capabilities?.[key];
  if (!entry || !Number.isInteger(entry.value)) return null;
  return { value: entry.value, used: Number.isInteger(entry.used) ? entry.used : null };
}

/** True when `used` has reached a finite `value`. Unlimited never is. */
export function isAtLimit(value, used) {
  return Number.isInteger(value) && value !== UNLIMITED && Number.isInteger(used) && used >= value;
}
